/* eslint-disable max-lines */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import toast from 'react-hot-toast';
import { Building2, Users, Plus, Eye, Trash2, PencilLine } from 'lucide-react';
import PageHeader from '@components/layout/page-header';
import Button from '@components/atoms/ui-kit/button';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';
import { useGetAdminOrganizations } from '@utils/api/admin/use-get-admin-organizations';
import { useDeleteAdminOrganization } from '@utils/api/admin/use-delete-admin-organization';
import { AdminOrganization } from '@utils/types/admin';
import DataGridV2, {
  DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller';
import { StatusBadge, Badge } from '@components/atoms/ui-kit/badge';
import OrganizationFormModal from './organization-form-modal';
import { PATHS } from '../../router/paths';

/**
 * On-Prem Admin Organizations List
 * Displays all organizations with user counts, session counts, storage usage.
 */
export default function AdminOrganizations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();

  const { data, isLoading, isFetching, isError } = useGetAdminOrganizations();
  const deleteMutation = useDeleteAdminOrganization();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] =
    useState<AdminOrganization | null>(null);

  const organizations = data?.result ?? [];
  const totalResults = data?.total_results ?? 0;

  const handleCreateOrganization = () => {
    setIsCreateModalOpen(true);
  };

  const handleViewOrganization = useCallback(
    (org: AdminOrganization) => {
      navigate(PATHS(String(org.id)).ADMIN_ORGANIZATION_DETAIL);
    },
    [navigate]
  );

  const handleEditOrganization = useCallback(
    (org: AdminOrganization) => {
      // Navigate to detail page where editing is available
      navigate(PATHS(String(org.id)).ADMIN_ORGANIZATION_DETAIL);
    },
    [navigate]
  );

  const handleDeleteOrganization = useCallback((org: AdminOrganization) => {
    setOrganizationToDelete(org);
    setDeleteModalOpen(true);
  }, []);

  const confirmDelete = () => {
    if (organizationToDelete) {
      deleteMutation.mutate(organizationToDelete.id, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setOrganizationToDelete(null);
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: {
              data?: {
                error_description?: string;
                detail?: string;
                message?: string;
              };
            };
          };
          const msg =
            err?.response?.data?.error_description ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            t('an-error-occurred');
          toast.error(msg);
        }
      });
    }
  };

  const columns: DataGridColumn<AdminOrganization>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('organization'),
        field: 'name',
        minWidth: 250,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'name',
        serverOperator: 'ilike',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              <Building2
                className="h-4 w-4"
                style={{ color: getColour('inactiveText') }}
              />
            </div>
            <span
              className="text-sm font-medium"
              style={{ color: getColour('normalText') }}
            >
              {row.original.name}
            </span>
          </div>
        )
      },
      {
        id: 'email',
        header: t('email'),
        field: 'email',
        minWidth: 250,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        filterKey: 'email',
        serverOperator: 'ilike',
        enableRowCopyOnClick: true,
        cell: ({ row }) => (
          <span
            className="text-sm"
            style={{ color: getColour('inactiveText') }}
          >
            {row.original.email}
          </span>
        )
      },
      {
        id: 'user_count',
        header: () => (
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            {t('users')}
          </div>
        ),
        field: 'user_count',
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => (
          <span className="text-sm" style={{ color: getColour('normalText') }}>
            {row.original.user_count}
          </span>
        )
      },
      {
        id: 'session_count',
        header: t('sessions'),
        field: 'session_count',
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => (
          <span className="text-sm" style={{ color: getColour('normalText') }}>
            {row.original.session_count}
          </span>
        )
      },
      {
        id: 'is_active',
        header: t('status'),
        field: 'is_active',
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => (
          <StatusBadge
            status={row.original.is_active ? 'active' : 'inactive'}
            label={row.original.is_active ? t('active') : t('inactive')}
          />
        )
      },
      {
        id: 'is_reseller',
        header: t('reseller'),
        field: 'is_reseller',
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) =>
          row.original.is_reseller ? (
            <Badge color="blue">{t('reseller')}</Badge>
          ) : (
            <span
              className="text-sm"
              style={{ color: getColour('inactiveText') }}
            >
              -
            </span>
          )
      }
    ],
    [t, getColour]
  );

  const handleRowClick = (row: AdminOrganization) => {
    handleViewOrganization(row);
  };

  const actions = useMemo<GridAction<AdminOrganization>[]>(
    () => [
      {
        id: 'view',
        label: t('view'),
        icon: <Eye className="h-3.5 w-3.5" />,
        run: row => handleViewOrganization(row)
      },
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-3.5 w-3.5" />,
        run: row => handleEditOrganization(row)
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-3.5 w-3.5" />,
        intent: 'danger',
        run: row => handleDeleteOrganization(row)
      }
    ],
    [
      t,
      handleViewOrganization,
      handleEditOrganization,
      handleDeleteOrganization
    ]
  );

  return (
    <div className="bg-app min-h-screen px-4 py-2">
      <PageHeader
        title={t('organizations')}
        caption={t('manage-all-organizations-in-the-system')}
      >
        <Button
          icon={<Plus width={14} />}
          onClick={handleCreateOrganization}
          label={t('new-organization')}
          variant="primary"
          size="small"
        />
      </PageHeader>

      {isError && (
        <p className="text-sm text-red-500 dark:text-red-400 mb-4">
          {t('an-error-occurred')}
        </p>
      )}

      <DataGridV2<AdminOrganization>
        data={organizations}
        columns={columns}
        actions={actions}
        loading={isLoading || isFetching || !data}
        enablePagination
        manualPagination
        enableSorting
        manualSorting
        enableFiltering
        manualFiltering
        totalItems={totalResults}
        pageSize={25}
        pageSizeOptions={[25, 50, 75, 100]}
        showColumnFilters={false}
        filterMode="popover"
        height="83vh"
        dense
        grid={false}
        striped
        onRowClick={handleRowClick}
        cursor="pointer"
        getRowId={row => String(row.id)}
      />

      {/* Create Organization Modal */}
      {isCreateModalOpen && (
        <OrganizationFormModal
          organization={null}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setOrganizationToDelete(null);
        }}
        onConfirm={confirmDelete}
        size="small"
        title={t('delete-organization')}
        caption={t('are-you-sure-you-want-to-delete-this-organization')}
        confirmLabel={deleteMutation.isLoading ? t('deleting') : t('delete')}
        cancelLabel={t('cancel')}
        confirmDisabled={deleteMutation.isLoading}
        destructive
        autoFocusConfirm
      />
    </div>
  );
}
